import { makeAutoObservable, runInAction } from 'mobx';

import { extractAccessToken } from 'common/helpers';
import {
    getUserInformation,
    getAllUserInformation,
    editUsersStatus,
    sendApprovalResultEmail,
    editUsersRole,
    editUserStatusWithEmployeeInformation
} from 'data/user/repositories/userCompanyRepository';
import { PutUserCompanyMappingResult } from 'data/user/schemas';
import { UserCompany, UserCompanyMappingOption } from 'models/user/UserCompany';
import RootStore from 'store/Root.store';
import Company from '../../components/company/Company';

class UserCompanyStore {
    private _mode = localStorage.getItem('mode') ?? '';

    private _userName = '';
    private _userRole = '';
    private _email = '';
    private _citizenId = '';
    private _firstName = '';
    private _lastName = '';
    private _userCompanyMappingOptions: UserCompanyMappingOption[] = [];
    private _userCompanyMapping: UserCompany[] = [];
    private _currentUserCompanyMapping: UserCompany[] = [];
    private _message = '';

    // Temporary
    private _currentAdminCompany: UserCompany[] = [];

    public get firstName() {
        return this._firstName;
    }

    public get lastName() {
        return this._lastName;
    }

    public get userName() {
        return this._userName;
    }

    public get userRole() {
        return this._userRole;
    }

    public get email() {
        return this._email;
    }

    public get citizenId() {
        return this._citizenId;
    }

    public get userCompanyMapping() {
        return this._userCompanyMapping;
    }

    public get currentUserCompanyMapping() {
        return this._currentUserCompanyMapping;
    }

    public get userCompanyMappingOptions() {
        return this._userCompanyMappingOptions;
    }

    public get message() {
        return this._message;
    }

    // Temporary
    public get currentAdminCompany() {
        return this._currentAdminCompany;
    }

    public get mode() {
        return this._mode;
    }

    public set mode(mode: string) {
        this._mode = mode;
        localStorage.setItem('mode', mode);
    }

    public updateUserStatusUI(
        citizenId: string,
        status: 'approve' | 'active' | 'rejected' | 'inactive',
        system_type: string
    ) {
        const user = this._userCompanyMapping.find(
            user => user.citizen_id === citizenId && user.system_type === system_type
        );
        if (!user) {
            console.warn(this.updateUserStatusUI.name, 'User status does not update');
            return;
        }

        user.status = status;

        // Change underlying array to force re-rendering
        this._userCompanyMapping = [...this._userCompanyMapping];
    }

    constructor(private rootStore: RootStore) {
        this.fetchUserFromLocalStorage();

        makeAutoObservable(this);
    }

    public fetchUserFromLocalStorage() {
        const extra = extractAccessToken();

        this._firstName = extra.first_name;
        this._lastName = extra.last_name;
        this._userName = extra.username;
        this._email = extra.email;
        this._citizenId = extra?.citizen_id?.toLowerCase() ?? null;
    }

    public async fetchUserCompany(sid: string, citizen_id: string, status: string = 'active') {
        const [error, responseUserCompany] = await getUserInformation({
            sid,
            status,
            citizen_id
        });

        if (error) {
            throw error;
        }

        this._setUser(responseUserCompany.result_list ?? []);
    }

    public async fetchAllUserCompany(
        sid: string,
        citizen_id: string,
        status: string = 'active',
        options?: { signal?: AbortSignal }
    ) {
        const [error, responseUserCompany] = await getAllUserInformation(
            {
                sid,
                status,
                citizen_id
            },
            options
        );

        if (error) {
            throw error;
        }

        this._setAllUser(responseUserCompany.result_list ?? []);

        return responseUserCompany.result_list;
    }

    public async sendApprovalResultEmail(
        status: 'approve' | 'active' | 'rejected' | 'inactive' | 'user_waiting',
        sid: string,
        citizen_id_list: string[],
        system_type: string
    ) {
        const [error] = await sendApprovalResultEmail({
            status,
            sid,
            citizen_id_list,
            system_type
        });
        if (error) throw error;

        runInAction(() => {
            this._message = 'Email sent';
        });
    }

    public async featchUpdateUsersStatus(
        status: string,
        sid: string,
        citizen_id_list: string[],
        system_type: string,
        reason?: string,
        inactive_workflow_date?: string,
        f1_username?: string,
        platform_id?: string,
        user_status_remark?: string,
        inactive_workflow?: boolean,
        inactive_dms?: boolean,
        active_workflow?: boolean,
        active_dms?: boolean,
        active_user_status_remark?: string
    ): Promise<PutUserCompanyMappingResult> {
        const [error, result] = await editUsersStatus({
            status,
            sid,
            citizen_id_list,
            system_type,
            reason,
            inactive_workflow_date,
            f1_username,
            platform_id,
            user_status_remark,
            inactive_workflow,
            inactive_dms,
            active_workflow,
            active_dms,
            active_user_status_remark
        });

        if (error) {
            throw error;
        }

        runInAction(() => {
            this._message = result?.message ?? '';
        });

        return result!;
    }

    public async fetchUpdateUserStatusWithEmployeeInformation(
        user: UserCompany,
        status: string,
        approveWorkflow?: boolean,
        approveDms?: boolean
    ) {
        if (!user.employee_information) {
            return;
        }

        const [error, result] = await editUserStatusWithEmployeeInformation({
            status,
            sid: user.sid,
            citizen_id_list: [user.citizen_id],
            employee_information: user.employee_information,
            system_type: user.system_type,
            approve_workflow: approveWorkflow,
            approve_dms: approveDms
        });
        if (error) throw error;

        runInAction(() => {
            this._message = result?.message ?? '';
        });

        return result!;
    }

    public async updateUsersRole(
        role: string,
        sid: string,
        citizen_id_list: string[],
        system_type
    ) {
        const [error, result] = await editUsersRole({
            type: role,
            sid,
            citizen_id_list,
            system_type
        });
        if (error) return [error, null];

        runInAction(() => {
            this._message = result?.message ?? '';
        });

        return [null, result];
    }

    private _setUser(userCompanies: UserCompany[]) {
        const user = userCompanies.filter(result => result.citizen_id === this._citizenId);
        if (user.length === 0) {
            return;
        }

        const role = this._getUserRole(user);
        const userCompanyMappingOptions = this._formatUserCompanyMapping(userCompanies);

        runInAction(() => {
            this._userRole = role;
            this._currentUserCompanyMapping = [...user];
            this._userCompanyMapping = [...userCompanies];
            this._userCompanyMappingOptions = userCompanyMappingOptions;
        });
    }

    // This method shouldn't exist
    private _setAllUser(userCompanies: UserCompany[]) {
        const userCompanyMappingOptions = this._formatUserCompanyMapping(userCompanies);

        runInAction(() => {
            this._userCompanyMapping = [...userCompanies];
            this._userCompanyMappingOptions = userCompanyMappingOptions;
        });
    }

    private _getUserRole(userCompanies: UserCompany[]) {
        const roles = new Map<string, string>([
            ['super_admin', 'Super Admin'],
            ['admin', 'Admin']
        ]);

        const foundAdminOrSuperAdminList = userCompanies.filter(
            user => user.type === 'super_admin' || user.type === 'admin'
        );

        if (!foundAdminOrSuperAdminList) {
            return 'User';
        }

        //if (foundAdminOrSuperAdmin.type === 'admin') {
        this._currentAdminCompany = foundAdminOrSuperAdminList;
        //}

        const foundSuperAdmin = userCompanies.find(user => user.type === 'super_admin');
        if (foundSuperAdmin) {
            return roles.get(foundSuperAdmin.type) ?? 'User';
        }

        const foundAdmin = userCompanies.find(user => user.type === 'admin');
        if (foundAdmin) {
            return roles.get(foundAdmin.type) ?? 'User';
        }

        return 'User';
    }

    private _formatUserCompanyMapping(userCompanies: UserCompany[]) {
        const options = userCompanies.map(
            userCompany =>
                ({
                    label: userCompany.company_name,
                    value: userCompany.sid
                }) as UserCompanyMappingOption
        );

        return options;
    }
}

export default UserCompanyStore;
